/**
 * Brushes & extended shapes primitives.
 *
 * Tools: Brush (freehand), Highlighter, Polyline, Arc,
 *        Circle, Arrow, Filled Rectangle, Filled Ellipse
 */

const HANDLE_RADIUS = 5;
const HANDLE_COLOR = '#6366f1';
const HANDLE_HIT = 8;
const HIT_TOLERANCE = 7;

function drawHandle(ctx, x, y, hpr, vpr) {
  ctx.beginPath();
  ctx.arc(x * hpr, y * vpr, HANDLE_RADIUS * hpr, 0, Math.PI * 2);
  ctx.fillStyle = HANDLE_COLOR; ctx.fill();
  ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5 * hpr; ctx.stroke();
}

function hexToRgba(hex, a) {
  const c = hex.replace('#', '');
  return `rgba(${parseInt(c.slice(0,2),16)},${parseInt(c.slice(2,4),16)},${parseInt(c.slice(4,6),16)},${a})`;
}

class Base {
  constructor() { this._chart = null; this._series = null; this._requestUpdate = null; this.isHovered = false; this.isSelected = false; this.lineWidth = undefined; this.lineStyle = undefined; }
  attached({ requestUpdate, chart, series }) { this._requestUpdate = requestUpdate; this._chart = chart; this._series = series; }
  detached() { this._requestUpdate = null; this._chart = null; this._series = null; }
  requestUpdate() { this._requestUpdate?.(); }
  hitTest() { return Infinity; }
  isNear(x, y) { return this.hitTest(x, y) <= HIT_TOLERANCE; }
  hitTestHandle() { return -1; }
  moveBy() {}
  moveHandle() {}
  _ts() { return this._chart?.timeScale(); }
  _toXY(logical, price) {
    return { x: this._ts()?.logicalToCoordinate(logical), y: this._series?.priceToCoordinate(price) };
  }
}

// ═══════════════════════════════════════════════════════════════════
// BRUSH — Freehand drawing (continuous mouse path)
// ═══════════════════════════════════════════════════════════════════

class BrushPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const pts = s._screenPts;
      if (!pts || pts.length < 2) return;
      ctx.save();

      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.2);
        ctx.lineWidth = (s.strokeWidth + 4) * hpr;
        ctx.lineCap = 'round'; ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(pts[0].x * hpr, pts[0].y * vpr);
        for (let i = 1; i < pts.length; i++) {
          if (pts[i].x == null) continue;
          ctx.lineTo(pts[i].x * hpr, pts[i].y * vpr);
        }
        ctx.stroke();
      }

      ctx.strokeStyle = s.isHighlighter ? hexToRgba(s.color, 0.35) : s.color;
      ctx.lineWidth = s.strokeWidth * hpr;
      ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(pts[0].x * hpr, pts[0].y * vpr);
      for (let i = 1; i < pts.length; i++) {
        if (pts[i].x == null) continue;
        ctx.lineTo(pts[i].x * hpr, pts[i].y * vpr);
      }
      ctx.stroke();

      if (s.isSelected && pts.length > 0) {
        drawHandle(ctx, pts[0].x, pts[0].y, hpr, vpr);
        drawHandle(ctx, pts[pts.length - 1].x, pts[pts.length - 1].y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class BrushPrimitive extends Base {
  constructor(points, options = {}) {
    super();
    this.points = [...points];
    this.color = options.color || '#f59e0b';
    this.strokeWidth = options.strokeWidth || 2;
    this.isHighlighter = options.isHighlighter || false;
    this.isFreehand = true;
    this._screenPts = [];
    this._view = new BrushPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._screenPts = this.points.map(p => this._toXY(p.logical, p.price));
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    const pts = this._screenPts;
    for (let i = 1; i < pts.length; i++) {
      const p1 = pts[i-1], p2 = pts[i];
      if (p1.x == null || p2.x == null) continue;
      const dx = p2.x - p1.x, dy = p2.y - p1.y, len2 = dx*dx + dy*dy;
      if (len2 === 0) continue;
      const t = Math.max(0, Math.min(1, ((x - p1.x)*dx + (y - p1.y)*dy) / len2));
      const d = Math.hypot(x - (p1.x + t*dx), y - (p1.y + t*dy));
      if (d <= this.strokeWidth + HIT_TOLERANCE) return d;
    }
    return Infinity;
  }
  hitTestHandle(x, y) {
    if (this._screenPts.length === 0) return -1;
    const first = this._screenPts[0], last = this._screenPts[this._screenPts.length - 1];
    if (first && Math.hypot(x - first.x, y - first.y) < HANDLE_HIT) return 0;
    if (last && Math.hypot(x - last.x, y - last.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.points = this.points.map(p => ({ logical: p.logical + dL, price: p.price + dP }));
    this._requestUpdate?.();
  }
  moveHandle() {} // no handle resize for freehand
  addPoint(pt) { this.points.push(pt); this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// POLYLINE — Multi-segment line (click to add, dbl-click to end)
// ═══════════════════════════════════════════════════════════════════

class PolylinePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const pts = s._screenPts;
      if (!pts || pts.length < 2) return;
      ctx.save();

      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.2);
        ctx.lineWidth = 6 * hpr;
        ctx.lineJoin = 'round'; ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(pts[0].x * hpr, pts[0].y * vpr);
        for (let i = 1; i < pts.length; i++) if (pts[i].x != null) ctx.lineTo(pts[i].x * hpr, pts[i].y * vpr);
        ctx.stroke();
      }

      ctx.strokeStyle = s.color;
      ctx.lineWidth = 2 * hpr;
      ctx.lineJoin = 'round'; ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(pts[0].x * hpr, pts[0].y * vpr);
      for (let i = 1; i < pts.length; i++) if (pts[i].x != null) ctx.lineTo(pts[i].x * hpr, pts[i].y * vpr);
      ctx.stroke();

      // Dots
      for (const pt of pts) {
        if (pt.x == null) continue;
        ctx.beginPath(); ctx.arc(pt.x * hpr, pt.y * vpr, 2.5 * hpr, 0, Math.PI * 2);
        ctx.fillStyle = s.color; ctx.fill();
      }

      if (s.isSelected) {
        for (const pt of pts) if (pt.x != null) drawHandle(ctx, pt.x, pt.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class PolylinePrimitive extends Base {
  constructor(points, options = {}) {
    super();
    this.points = [...points];
    this.color = options.color || '#f59e0b';
    this._screenPts = [];
    this._view = new PolylinePV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._screenPts = this.points.map(p => this._toXY(p.logical, p.price));
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    const pts = this._screenPts;
    for (let i = 1; i < pts.length; i++) {
      const p1 = pts[i-1], p2 = pts[i];
      if (p1.x == null || p2.x == null) continue;
      const dx = p2.x - p1.x, dy = p2.y - p1.y, len2 = dx*dx + dy*dy;
      if (len2 === 0) continue;
      const t = Math.max(0, Math.min(1, ((x - p1.x)*dx + (y - p1.y)*dy) / len2));
      const d = Math.hypot(x - (p1.x + t*dx), y - (p1.y + t*dy));
      if (d <= HIT_TOLERANCE) return d;
    }
    return Infinity;
  }
  hitTestHandle(x, y) {
    for (let i = 0; i < this._screenPts.length; i++) {
      const p = this._screenPts[i];
      if (p && Math.hypot(x - p.x, y - p.y) < HANDLE_HIT) return i;
    }
    return -1;
  }
  moveBy(dL, dP) {
    this.points = this.points.map(p => ({ logical: p.logical + dL, price: p.price + dP }));
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i >= 0 && i < this.points.length) { this.points[i] = { logical: l, price: p }; this._requestUpdate?.(); }
  }
  addPoint(pt) { this.points.push(pt); this._requestUpdate?.(); }
  setPreviewPoint(pt) {
    if (this.points.length > 0) { this.points[this.points.length - 1] = pt; this._requestUpdate?.(); }
  }
}

// ═══════════════════════════════════════════════════════════════════
// ARC — Curved arc between two points (2 clicks)
// ═══════════════════════════════════════════════════════════════════

class ArcPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;
      const x1 = p1.x * hpr, y1 = p1.y * vpr;
      const x2 = p2.x * hpr, y2 = p2.y * vpr;
      const midX = (x1 + x2) / 2;
      const midY = (y1 + y2) / 2;
      const dist = Math.hypot(x2 - x1, y2 - y1);
      // Control point perpendicular to the midpoint
      const nx = -(y2 - y1) / dist, ny = (x2 - x1) / dist;
      const bulge = dist * 0.4;
      const cpx = midX + nx * bulge, cpy = midY + ny * bulge;

      ctx.save();

      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.2);
        ctx.lineWidth = 6 * hpr;
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.quadraticCurveTo(cpx, cpy, x2, y2); ctx.stroke();
      }

      ctx.strokeStyle = s.color;
      ctx.lineWidth = 2 * hpr;
      ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.quadraticCurveTo(cpx, cpy, x2, y2); ctx.stroke();

      if (s.isSelected) {
        drawHandle(ctx, p1.x, p1.y, hpr, vpr);
        drawHandle(ctx, p2.x, p2.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class ArcPrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super();
    this.pt1 = pt1; this.pt2 = pt2;
    this.color = options.color || '#f59e0b';
    this._p1 = null; this._p2 = null;
    this._view = new ArcPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    // Sample points along the curve and check distance
    const x1 = this._p1.x, y1 = this._p1.y, x2 = this._p2.x, y2 = this._p2.y;
    const dist = Math.hypot(x2 - x1, y2 - y1);
    if (dist < 1) return Infinity;
    const nx = -(y2 - y1) / dist, ny = (x2 - x1) / dist;
    const midX = (x1 + x2) / 2, midY = (y1 + y2) / 2;
    const cpx = midX + nx * dist * 0.4, cpy = midY + ny * dist * 0.4;
    for (let t = 0; t <= 1; t += 0.05) {
      const qx = (1-t)*(1-t)*x1 + 2*(1-t)*t*cpx + t*t*x2;
      const qy = (1-t)*(1-t)*y1 + 2*(1-t)*t*cpy + t*t*y2;
      if (Math.hypot(x - qx, y - qy) <= HIT_TOLERANCE) return 0;
    }
    return Infinity;
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x - this._p1.x, y - this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x - this._p2.x, y - this._p2.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = { logical: this.pt1.logical + dL, price: this.pt1.price + dP };
    this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) { if (i === 0) this.pt1 = { logical: l, price: p }; else this.pt2 = { logical: l, price: p }; this._requestUpdate?.(); }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// ARROW — Line with arrowhead (2 clicks)
// ═══════════════════════════════════════════════════════════════════

class ArrowLinePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;
      const x1 = p1.x * hpr, y1 = p1.y * vpr;
      const x2 = p2.x * hpr, y2 = p2.y * vpr;

      ctx.save();

      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.2);
        ctx.lineWidth = 6 * hpr;
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      }

      ctx.strokeStyle = s.color;
      ctx.lineWidth = 2 * hpr;
      ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();

      // Arrowhead
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const al = 12 * hpr;
      ctx.fillStyle = s.color;
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - al * Math.cos(angle - 0.4), y2 - al * Math.sin(angle - 0.4));
      ctx.lineTo(x2 - al * Math.cos(angle + 0.4), y2 - al * Math.sin(angle + 0.4));
      ctx.closePath();
      ctx.fill();

      if (s.isSelected) {
        drawHandle(ctx, p1.x, p1.y, hpr, vpr);
        drawHandle(ctx, p2.x, p2.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class ArrowLinePrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super();
    this.pt1 = pt1; this.pt2 = pt2;
    this.color = options.color || '#f59e0b';
    this._p1 = null; this._p2 = null;
    this._view = new ArrowLinePV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    const dx = this._p2.x - this._p1.x, dy = this._p2.y - this._p1.y, len2 = dx*dx + dy*dy;
    if (len2 === 0) return Infinity;
    const t = Math.max(0, Math.min(1, ((x - this._p1.x)*dx + (y - this._p1.y)*dy) / len2));
    return Math.hypot(x - (this._p1.x + t*dx), y - (this._p1.y + t*dy));
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x - this._p1.x, y - this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x - this._p2.x, y - this._p2.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = { logical: this.pt1.logical + dL, price: this.pt1.price + dP };
    this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) { if (i === 0) this.pt1 = { logical: l, price: p }; else this.pt2 = { logical: l, price: p }; this._requestUpdate?.(); }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// CIRCLE — Perfect circle defined by center + edge (2 clicks)
// ═══════════════════════════════════════════════════════════════════

class CirclePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const c = s._center, e = s._edge;
      if (!c || !e || c.x == null || e.x == null) return;
      const cx = c.x * hpr, cy = c.y * vpr;
      const r = Math.hypot((e.x - c.x) * hpr, (e.y - c.y) * vpr);

      ctx.save();

      if (s.filled) {
        ctx.fillStyle = hexToRgba(s.color, 0.1);
        ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
      }

      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.2);
        ctx.lineWidth = 6 * hpr;
        ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
      }

      ctx.strokeStyle = s.color;
      ctx.lineWidth = 1.5 * hpr;
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();

      if (s.isSelected) {
        drawHandle(ctx, c.x, c.y, hpr, vpr);
        drawHandle(ctx, e.x, e.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class CirclePrimitive extends Base {
  constructor(centerPt, edgePt, options = {}) {
    super();
    this.centerPt = centerPt; this.edgePt = edgePt;
    this.color = options.color || '#f59e0b';
    this.filled = options.filled || false;
    this._center = null; this._edge = null;
    this._view = new CirclePV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._center = this._toXY(this.centerPt.logical, this.centerPt.price);
    this._edge = this._toXY(this.edgePt.logical, this.edgePt.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._center || !this._edge) return Infinity;
    const r = Math.hypot(this._edge.x - this._center.x, this._edge.y - this._center.y);
    const dist = Math.hypot(x - this._center.x, y - this._center.y);
    if (this.filled && dist < r) return 0;
    return Math.abs(dist - r) <= HIT_TOLERANCE ? Math.abs(dist - r) : Infinity;
  }
  hitTestHandle(x, y) {
    if (this._center && Math.hypot(x - this._center.x, y - this._center.y) < HANDLE_HIT) return 0;
    if (this._edge && Math.hypot(x - this._edge.x, y - this._edge.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.centerPt = { logical: this.centerPt.logical + dL, price: this.centerPt.price + dP };
    this.edgePt = { logical: this.edgePt.logical + dL, price: this.edgePt.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i === 0) this.centerPt = { logical: l, price: p };
    else this.edgePt = { logical: l, price: p };
    this._requestUpdate?.();
  }
  setPreviewPoint(pt) { this.edgePt = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// FILLED RECTANGLE — Rectangle with fill (2 clicks)
// ═══════════════════════════════════════════════════════════════════

class FilledRectPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;
      const x1 = p1.x * hpr, y1 = p1.y * vpr;
      const x2 = p2.x * hpr, y2 = p2.y * vpr;
      const rx = Math.min(x1, x2), ry = Math.min(y1, y2);
      const rw = Math.abs(x2 - x1), rh = Math.abs(y2 - y1);

      ctx.save();

      ctx.fillStyle = hexToRgba(s.color, 0.15);
      ctx.fillRect(rx, ry, rw, rh);

      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.4);
        ctx.lineWidth = 2 * hpr;
      } else {
        ctx.strokeStyle = s.color;
        ctx.lineWidth = 1.5 * hpr;
      }
      ctx.strokeRect(rx, ry, rw, rh);

      if (s.isSelected) {
        drawHandle(ctx, p1.x, p1.y, hpr, vpr);
        drawHandle(ctx, p2.x, p2.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class FilledRectPrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super();
    this.pt1 = pt1; this.pt2 = pt2;
    this.color = options.color || '#f59e0b';
    this._p1 = null; this._p2 = null;
    this._view = new FilledRectPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    const minX = Math.min(this._p1.x, this._p2.x), maxX = Math.max(this._p1.x, this._p2.x);
    const minY = Math.min(this._p1.y, this._p2.y), maxY = Math.max(this._p1.y, this._p2.y);
    return (x >= minX && x <= maxX && y >= minY && y <= maxY) ? 0 : Infinity;
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x - this._p1.x, y - this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x - this._p2.x, y - this._p2.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = { logical: this.pt1.logical + dL, price: this.pt1.price + dP };
    this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) { if (i === 0) this.pt1 = { logical: l, price: p }; else this.pt2 = { logical: l, price: p }; this._requestUpdate?.(); }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// FILLED ELLIPSE — Ellipse with fill (2 clicks)
// ═══════════════════════════════════════════════════════════════════

class FilledEllipsePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;
      const cx = (p1.x + p2.x) / 2 * hpr, cy = (p1.y + p2.y) / 2 * vpr;
      const rx = Math.abs(p2.x - p1.x) / 2 * hpr;
      const ry = Math.abs(p2.y - p1.y) / 2 * vpr;

      ctx.save();

      ctx.fillStyle = hexToRgba(s.color, 0.12);
      ctx.beginPath(); ctx.ellipse(cx, cy, Math.max(rx, 1), Math.max(ry, 1), 0, 0, Math.PI * 2); ctx.fill();

      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.4);
        ctx.lineWidth = 2 * hpr;
      } else {
        ctx.strokeStyle = s.color;
        ctx.lineWidth = 1.5 * hpr;
      }
      ctx.beginPath(); ctx.ellipse(cx, cy, Math.max(rx, 1), Math.max(ry, 1), 0, 0, Math.PI * 2); ctx.stroke();

      if (s.isSelected) {
        drawHandle(ctx, p1.x, p1.y, hpr, vpr);
        drawHandle(ctx, p2.x, p2.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class FilledEllipsePrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super();
    this.pt1 = pt1; this.pt2 = pt2;
    this.color = options.color || '#f59e0b';
    this._p1 = null; this._p2 = null;
    this._view = new FilledEllipsePV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    const cx = (this._p1.x + this._p2.x) / 2, cy = (this._p1.y + this._p2.y) / 2;
    const rx = Math.abs(this._p2.x - this._p1.x) / 2, ry = Math.abs(this._p2.y - this._p1.y) / 2;
    if (rx < 1 || ry < 1) return Infinity;
    const v = ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2;
    return v <= 1 ? 0 : Infinity;
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x - this._p1.x, y - this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x - this._p2.x, y - this._p2.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = { logical: this.pt1.logical + dL, price: this.pt1.price + dP };
    this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) { if (i === 0) this.pt1 = { logical: l, price: p }; else this.pt2 = { logical: l, price: p }; this._requestUpdate?.(); }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}
